import api from '../../services/api';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Async thunk for getting all subjects
export const getSubjectsList = createAsyncThunk(
  'subjects/list',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        url: '/subject/',
        method: 'GET',
      });
      return response?.data;
    } catch (error) {
      console.log('Error fetching subjects:', error);
      return rejectWithValue(
        error.response?.data || 'Failed to fetch subjects',
      );
    }
  },
);

// Async thunk for creating a new subject
export const createSubject = createAsyncThunk(
  'subjects/create',
  async (subjectData, { rejectWithValue }) => {
    try {
      const payload = {
        subjectName: subjectData.subjectName,
      };
      const response = await api({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        url: '/subject/',
        method: 'POST',
        data: payload,
      });
      return response?.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to create subject',
      );
    }
  },
);

// Async thunk for updating a subject
export const updateSubject = createAsyncThunk(
  'subjects/update',
  async (subjectData, { rejectWithValue }) => {
    try {
      const payload = {
        ...subjectData,
      };
      const response = await api({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        url: `/subject/`,
        method: 'PUT',
        data: payload,
      });
      return { id: subjectData.id, data: response?.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to update subject',
      );
    }
  },
);

// Async thunk for deleting a subject
export const deleteSubject = createAsyncThunk(
  'subjects/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        url: `/subject/${id}`,
        method: 'DELETE',
      });
      return { id, status: response?.status };
    } catch (error) {
      console.log('Error deleting subject:', error);
      return rejectWithValue(
        error.response?.data || 'Failed to delete subject',
      );
    }
  },
);

const subjectSlice = createSlice({
  name: 'subjects',
  initialState: {
    subjectsList: [],
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
  },
  reducers: {
    // Clear error message
    clearSubjectError: (state) => {
      state.error = null;
    },
    // Reset status to idle
    resetSubjectStatus: (state) => {
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get subjects list cases
      .addCase(getSubjectsList.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getSubjectsList.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.subjectsList = action.payload || [];
        state.error = null;
      })
      .addCase(getSubjectsList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      // Create subject cases
      .addCase(createSubject.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createSubject.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Add the new subject to the list if it's provided
        if (action.payload) {
          state.subjectsList.push(action.payload);
        }
        state.error = null;
      })
      .addCase(createSubject.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      // Update subject cases
      .addCase(updateSubject.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateSubject.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Update the subject in the list
        const { id, data } = action.payload;
        const existingSubjectIndex = state.subjectsList.findIndex(
          (subject) => subject.id === id,
        );
        if (existingSubjectIndex !== -1 && data) {
          state.subjectsList[existingSubjectIndex] = data;
        }
        state.error = null;
      })
      .addCase(updateSubject.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      // Delete subject cases
      .addCase(deleteSubject.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(deleteSubject.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Remove the subject from the list
        const { id } = action.payload;
        state.subjectsList = state.subjectsList.filter(
          (subject) => subject.id !== id,
        );
        state.error = null;
      })
      .addCase(deleteSubject.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });
  },
});

// Export reducer
export default subjectSlice.reducer;
